import { store } from '../../store/store';
import i18n from 'i18next';
import { saveProfileInLocalStorage, sendPushToken } from "../../services/AuthService";
import indexedDBService from '../../services/IndexedDBService';

export function restaurantHasDelivery() {
  const restaurant = store.getState().auth?.auth?.restaurant;
  const deliveryTypes = restaurant?.order_types?.['delivery'] || restaurant?.order_types?.delivery;
  if (deliveryTypes && typeof deliveryTypes === 'object') {
    return Object.values(deliveryTypes).includes(true);
  }
  return false;
}

export function restaurantHasQRpay() {
  const restaurant = store.getState().auth?.auth?.restaurant;
  return Boolean(restaurant?.qr_pay);
}

export function getDistanceInKm(point1, point2) {
  const lat1 = parseFloat(point1.lat);
  const lng1 = parseFloat(point1.lng);
  const lat2 = parseFloat(point2.lat);
  const lng2 = parseFloat(point2.lng);
  const earthRadiusKm = 6371;
  const latDiffInKm = (lat2 - lat1) * (Math.PI / 180) * earthRadiusKm;
  const lngDiffInKm = (lng2 - lng1) * (Math.PI / 180) * earthRadiusKm * Math.cos((lat1 + lat2) * (Math.PI / 360));
  return Math.hypot(latDiffInKm, lngDiffInKm);
}

export function translateRecord(item, field) {
  if (i18n.language === 'en') {
    return !!item[`${field}_en`] ? item[`${field}_en`] : item[field];
  } else {
    return item[field];
  }
}

export function isFutureTime(time) {
  const selectedTime = new Date();
  selectedTime.setHours(time.getHours());
  selectedTime.setMinutes(time.getMinutes());
  return new Date().getTime() < selectedTime.getTime();
}

export function formatEmoji(emoji, fallback = '') {
  if (!emoji) return fallback;
  try {
    const emojiStr = String(emoji).trim();
    if (emojiStr.startsWith('0x') || emojiStr.startsWith('0X')) {
      return String.fromCodePoint(parseInt(emojiStr, 16));
    }
    if (/^[0-9a-fA-F]+$/.test(emojiStr) && emojiStr.length >= 4) {
      return String.fromCodePoint(parseInt(emojiStr, 16));
    }
    if (!isNaN(Number(emojiStr))) {
      return String.fromCodePoint(Number(emojiStr));
    }
    if (emojiStr.includes(',')) {
      return String.fromCodePoint(...emojiStr.split(',').map(e => Number(e.trim())));
    }
    return emojiStr;
  } catch (e) {
    return fallback;
  }
}

function dishMatchesSaleItems(items, dishId, dishCatId, dishSubCatId, dishParentId) {
  if (!items || items.length === 0) return true;
  return items.some(item => {
    if (item.type === 'dish' && Number(item.id) === dishId) return true;
    if (item.type === 'category' || item.type === 'CategoryDish') {
      const itemCatId = Number(item.id);
      if (
        (dishCatId && itemCatId === dishCatId) ||
        (dishSubCatId && itemCatId === dishSubCatId) ||
        (dishParentId && itemCatId === dishParentId)
      ) return true;
    }
    return false;
  });
}

export function isSaleScheduleAvailable(sale, targetDate = new Date()) {
  if (!sale || sale.active === false) return false;

  const dateObj = targetDate instanceof Date ? targetDate : new Date(targetDate);
  if (isNaN(dateObj.getTime())) return true;

  const pad = n => (n < 10 ? '0' + n : n);
  const currentYMD = `${dateObj.getFullYear()}-${pad(dateObj.getMonth() + 1)}-${pad(dateObj.getDate())}`;

  if (sale.start_date && currentYMD < sale.start_date) return false;
  if (sale.end_date && currentYMD > sale.end_date) return false;

  if (sale.days_of_week && Array.isArray(sale.days_of_week) && sale.days_of_week.length > 0) {
    const rawWday = dateObj.getDay();
    const currentWday = rawWday === 0 ? 7 : rawWday;
    const daysArr = sale.days_of_week.map(Number);
    if (!daysArr.includes(currentWday)) return false;
  }

  if (sale.start_time && sale.end_time) {
    const nowMinutes = dateObj.getHours() * 60 + dateObj.getMinutes();
    const [stH, stM] = sale.start_time.split(':').map(Number);
    const [etH, etM] = sale.end_time.split(':').map(Number);

    if (!isNaN(stH) && !isNaN(etH)) {
      const stMinutes = stH * 60 + (stM || 0);
      const etMinutes = etH * 60 + (etM || 0);

      if (stMinutes <= etMinutes) {
        if (nowMinutes < stMinutes || nowMinutes > etMinutes) return false;
      } else {
        if (nowMinutes < stMinutes && nowMinutes > etMinutes) return false;
      }
    }
  }

  return true;
}

export function isSaleAvailable(sale, selectedOrderType = null, targetDate = new Date()) {
  if (!isSaleScheduleAvailable(sale, targetDate)) return false;

  if (selectedOrderType && sale.excluded_order_types && sale.excluded_order_types.includes(selectedOrderType)) {
    return false;
  }

  return true;
}

export function getDishSaleDiscount(dish, salesList = [], categoryId = null, selectedOrderType = null, variation = null) {
  if (!dish || dish.is_sale) {
    return { hasDiscount: false, discountPercentage: 0 };
  }

  let listToUse = salesList;
  if (!listToUse || !listToUse.length) {
    try {
      const storeState = store.getState();
      listToUse = storeState.restaurants?.publicSales || [];
    } catch (e) {}
  }

  if (!listToUse || !listToUse.length) {
    return { hasDiscount: false, discountPercentage: 0 };
  }

  const targetObj = variation || dish;
  const origPrice = parseFloat(targetObj.original_price || targetObj.price || 0);
  if (origPrice <= 0) {
    return { hasDiscount: false, discountPercentage: 0 };
  }

  const dishRestId = Number(dish.restaurant_id || dish.restaurant?.id);
  const dishRestSlug = dish.restaurant_slug || dish.restaurant?.slug;

  const activeSales = listToUse.filter(s =>
    isSaleAvailable(s, selectedOrderType) &&
    (
      !dishRestId ||
      s.restaurant_id == dishRestId ||
      s.restaurant?.id == dishRestId ||
      (dishRestSlug && s.restaurant?.slug === dishRestSlug)
    )
  );

  if (!activeSales.length) {
    return { hasDiscount: false, discountPercentage: 0 };
  }

  const dishId = Number(dish.id || dish.dish_restaurant_id);
  const dishCatId = Number(dish.category_dish_id || dish.category_id);
  const dishSubCatId = Number(dish.sub_category_id);
  const dishParentId = Number(dish.category_parent_id || dish.parent_id);

  let bestDiscountedPrice = origPrice;
  let bestDiscountPercentage = 0;
  let hasDiscount = false;
  let bestSaleType = null;

  activeSales.forEach(sale => {
    const items = sale.items || [];
    const isPercentage = sale.discount_type === 'percentage';
    const isFixed = sale.discount_type === 'fixed' || (!isPercentage && parseFloat(sale.price) > 0);

    if (isPercentage) {
      const matches = dishMatchesSaleItems(items, dishId, dishCatId, dishSubCatId, dishParentId);
      if (matches) {
        const pct = parseFloat(sale.discount_percentage || 0);
        if (pct > 0) {
          const discPrice = parseFloat((origPrice * (1 - pct / 100)).toFixed(2));
          if (discPrice < bestDiscountedPrice) {
            bestDiscountedPrice = discPrice;
            bestDiscountPercentage = pct;
            hasDiscount = true;
            bestSaleType = 'percentage';
          }
        }
      }
    } else if (isFixed && items.length > 0) {
      const matches = dishMatchesSaleItems(items, dishId, dishCatId, dishSubCatId, dishParentId);
      if (matches) {
        const saleFixedPrice = parseFloat(sale.price || 0);
        if (saleFixedPrice > 0 && saleFixedPrice < origPrice) {
          const pct = Math.round((1 - saleFixedPrice / origPrice) * 100);
          if (saleFixedPrice < bestDiscountedPrice) {
            bestDiscountedPrice = saleFixedPrice;
            bestDiscountPercentage = pct;
            hasDiscount = true;
            bestSaleType = 'fixed';
          }
        }
      }
    }
  });

  if (hasDiscount) {
    return {
      hasDiscount: true,
      discountPercentage: bestDiscountPercentage,
      originalPrice: origPrice,
      discountedPrice: parseFloat(bestDiscountedPrice.toFixed(2)),
      saleType: bestSaleType,
      isFixedPrice: bestSaleType === 'fixed'
    };
  }

  return { hasDiscount: false, discountPercentage: 0 };
}

export function formatSaleTimeAndDueDate(sale) {
  if (!sale) return null;

  let timeStr = '';
  if (sale.start_time && sale.end_time) {
    timeStr = `${sale.start_time}-${sale.end_time}`;
  } else if (sale.start_time) {
    timeStr = `з ${sale.start_time}`;
  } else if (sale.end_time) {
    timeStr = `до ${sale.end_time}`;
  }

  let dueStr = '';
  if (sale.end_date) {
    const endDateStr = String(sale.end_date).slice(0, 10);
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;

    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tYear = tomorrow.getFullYear();
    const tMonth = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const tDay = String(tomorrow.getDate()).padStart(2, '0');
    const tomorrowStr = `${tYear}-${tMonth}-${tDay}`;

    if (endDateStr === todayStr) {
      dueStr = 'до сьогодні';
    } else if (endDateStr === tomorrowStr) {
      dueStr = 'до завтра';
    }
  }

  if (timeStr && dueStr) {
    return `${timeStr} (${dueStr})`;
  } else if (timeStr) {
    return timeStr;
  } else if (dueStr) {
    return dueStr;
  }
  return null;
}

export function getPackingPrice(slug) {
  let result = 0;
  const storeState = store.getState();
  const r = storeState.orders.orderDishes[slug];
  if (!!r) {
    const restaurantObj = storeState.restaurants.restaurants?.[slug] || r.restaurant;
    const salesList = restaurantObj?.sales || [];
    const selectedOrderType = storeState.orders.orderCheckout?.selectedOrderType;
    const dishOrders = Object.values(r.dishes);
    result += dishOrders.filter((d) => !d.package_capacity || d.package_capacity === 1).map((d) => boxPrice(d, salesList, selectedOrderType) * (calculateQuantity(d) || 1)).reduce((partialSum, a) => partialSum + a, 0);
    const groupPackages = groupBy(dishOrders.filter((d) => d.package_capacity > 1), 'sub_category_id');
    result += Object.values(groupPackages).map((dishes) =>
      Math.ceil(dishes.map((d) => calculateCapacity(d)).reduce((partialSum, a) => partialSum + a, 0)) *
      boxPrice(dishes[0], salesList, selectedOrderType)).reduce((partialSum, a) => partialSum + a, 0);
    if (!!r.restaurant.cutleryOrders && storeState.orders.orderCheckout.cutleries) {
      result += Object.values(r.restaurant.cutleryOrders).map((c) => c.price * c.quantity).reduce((partialSum, a) => partialSum + a, 0);
    }
    if (dishOrders.map((d) => d.categoryPackage).includes(true)) {
      let rawPkgPrice = parseFloat(r.restaurant.package_price || 0);
      let maxPkgDiscountPct = 0;
      if (salesList && salesList.length) {
        const activePctSales = salesList.filter(s =>
          s.discount_type === 'percentage' &&
          s.active !== false &&
          (!selectedOrderType || !s.excluded_order_types || !s.excluded_order_types.includes(selectedOrderType))
        );
        activePctSales.forEach(sale => {
          const items = sale.items || [];
          if (items.length === 0) {
            if (parseFloat(sale.discount_percentage) > maxPkgDiscountPct) {
              maxPkgDiscountPct = parseFloat(sale.discount_percentage);
            }
          } else {
            const matches = dishOrders.some(d => {
              const dId = Number(d.id || d.dish_restaurant_id);
              const cId = Number(d.category_dish_id || d.category_id);
              const pId = Number(d.category_parent_id || d.parent_id);
              return items.some(si =>
                (si.type === 'dish' && Number(si.id) === dId) ||
                (si.type === 'category' && (Number(si.id) === cId || (pId && Number(si.id) === pId)))
              );
            });
            if (matches && parseFloat(sale.discount_percentage) > maxPkgDiscountPct) {
              maxPkgDiscountPct = parseFloat(sale.discount_percentage);
            }
          }
        });
      }
      const discPkgPrice = maxPkgDiscountPct > 0 ? parseFloat((rawPkgPrice * (1.0 - maxPkgDiscountPct / 100.0)).toFixed(2)) : rawPkgPrice;
      return (result + discPkgPrice);
    }
  }
  return result;
}

function groupBy(xs, key) {
  return xs.reduce(function (rv, x) {
    if (!!x[key]) {
      (rv[x[key]] = rv[x[key]] || []).push(x);
    } else {
      (rv[`${x['category_dish_id']}_cd`] = rv[`${x['category_dish_id']}_cd`] || []).push(x);
    }
    return rv;
  }, {});
}

export function orderSummary(s) {
  const storeOrders = store.getState().orders;
  if (!!storeOrders.orderDishes[s]) {
    let result = dishesAmount(s);
    if (storeOrders.orderCheckout.selectedOrderType === 'to_go' ||
      storeOrders.orderCheckout.selectedOrderType === 'delivery') {
      return result + getPackingPrice(s);
    } else {
      return result;
    }
  } else {
    return 0;
  }
}

export function dishesAmount(s) {
  const storeState = store.getState();
  const storeOrders = storeState.orders;
  const restaurant = storeOrders.orderDishes[s];
  if (!!restaurant) {
    const restaurantObj = storeState.restaurants.restaurants?.[s] || restaurant.restaurant;
    const salesList = restaurantObj?.sales || [];
    const selectedOrderType = storeOrders.orderCheckout?.selectedOrderType;

    let summary = [];
    let checkedCategories = [];
    Object.values(restaurant.dishes).forEach((dish) => {
      let currentPrice = parseFloat(dish.price || 0);

      if (!dish.is_sale) {
        const basePrice = parseFloat(dish.original_price || dish.price || 0);
        const discountInfo = getDishSaleDiscount(dish, salesList, dish.category_dish_id || dish.category_id, selectedOrderType);
        if (discountInfo.hasDiscount) {
          currentPrice = discountInfo.discountedPrice;
        } else {
          currentPrice = basePrice;
        }
      }

      if (!!dish.selectedVariation) {
        let varPrice = parseFloat(dish.selectedVariation.price || 0);
        if (!dish.is_sale) {
          const baseVarPrice = parseFloat(dish.selectedVariation.original_price || dish.selectedVariation.price || 0);
          const discountInfo = getDishSaleDiscount(dish, salesList, dish.category_dish_id || dish.category_id, selectedOrderType, dish.selectedVariation);
          if (discountInfo.hasDiscount) {
            varPrice = discountInfo.discountedPrice;
          } else {
            varPrice = baseVarPrice;
          }
        }

        if (!checkedCategories.includes(dish.category_dish_id) && !!dish.categoryPrice) {
          checkedCategories.push(dish.category_dish_id);
          summary.push(parseFloat(dish.categoryPrice));
        } else if (!dish.categoryPrice) {
          summary.push(varPrice * parseFloat(dish.quantity));
        }
      } else {
        if (dish.weight_flag) {
          summary.push((currentPrice / parseFloat(dish.weight)) * parseFloat(dish.quantity));
        } else {
          summary.push(currentPrice * parseFloat(dish.quantity));
        }
      }
      if (!!dish.selectedMods) {
        Object.values(dish.selectedMods).forEach((mod) =>
          summary.push(parseFloat(mod.price) * parseFloat(mod.quantity) * parseFloat(dish.quantity))
        );
      }
    });
    return summary.reduce((partialSum, a) => partialSum + a, 0);
  } else {
    return 0;
  }
}

function calculatePrice(dish) {
  if (dish.weight_flag) {
    return (parseFloat(dish.price) / parseFloat(dish.weight)) * parseFloat(dish.quantity)
  } else {
    return parseFloat(dish.price) * parseFloat(dish.quantity);
  }
}

function calculateCapacity(dish) {
  if (!!dish.dish_restaurant_id) {
    return (1 / dish.package_capacity) * (calculateQuantity(dish) || 1);
  } else {
    return dish.weight_flag ?
      dish.quantity / dish.package_capacity : (1 / dish.package_capacity) * (calculateQuantity(dish) || 1);
  }
}

function calculateQuantity(dish) {
  return dish.weight_flag && !dish.dish_restaurant_id ?
    parseFloat(dish.quantity) / parseFloat(dish.weight) : parseFloat(dish.quantity);
}

export function boxPrice(dish, salesList = [], selectedOrderType = null) {
  const rawPrice = (!!dish?.selectedVariation ? parseFloat(dish.selectedVariation.box?.price || dish.selectedVariation.box_price || 0) : parseFloat(dish?.box?.price || dish?.box_price || 0)) || 0;
  if (rawPrice <= 0 || !dish) return rawPrice;

  let maxDiscountPct = 0;
  if (dish.is_sale && dish.discount_type === 'percentage' && dish.discount_percentage) {
    if (!selectedOrderType || !dish.excluded_order_types || !dish.excluded_order_types.includes(selectedOrderType)) {
      maxDiscountPct = parseFloat(dish.discount_percentage);
    }
  } else {
    const discountInfo = getDishSaleDiscount(dish, salesList, dish.category_dish_id || dish.category_id, selectedOrderType);
    if (discountInfo.hasDiscount) {
      if (discountInfo.isFixedPrice) {
        return 0;
      }
      maxDiscountPct = discountInfo.discountPercentage;
    }
  }

  if (maxDiscountPct > 0) {
    return parseFloat((rawPrice * (1.0 - maxDiscountPct / 100.0)).toFixed(2));
  }
  return rawPrice;
}

export function isOrderActive(o) {
  return o.status === 'created' || o.status === 'payed' || o.status === 'cooking' || o.status === 'ready' ||
    o.status === 'on_delivery';
}

export const payCheckDishes = (payCheck) => {
  if (!!payCheck) {
    return Object.values(payCheck.orders).reduce((acc, order) => {
      const guestId = order.guest_id;
      if (!acc[guestId]) {
        acc[guestId] = [];
      }
      acc[guestId].push(order);
      return acc;
    }, {});
  } else {
    return [];
  }
};

export function isIOSWebView() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    if (!/Safari/.test(userAgent)) {
      return true;
    }
  }
  return false;
}

export const isRNappWebView = typeof window.ReactNativeWebView !== "undefined";

export const isTerminal = (() => {
  let cachedValue = null;
  let isInitialized = false;

  const initTerminal = async () => {
    if (isRNappWebView) {
      const terminalValue = await indexedDBService.getSetting('isTerminal');
      cachedValue = !!terminalValue;
    }
    isInitialized = true;
  };

  initTerminal();

  return () => {
    if (isRNappWebView) {
      if (!isInitialized) {
        return false;
      }
      return cachedValue;
    } else {
      return window.location.pathname.includes('1189');
    }
  };
})();

//export const isTerminal = window.location.host === 'localhost';//'192.168.1.200';

export function isOpenedInApp() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone ||
    window.webkit?.messageHandlers.standalone || isIOSWebView() || isRNappWebView
}

export function isMobileSafari() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  if ((/iPhone|iPad/i.test(userAgent)) && !window.MSStream) {
    if (/Safari/.test(userAgent) && !/CriOS/.test(userAgent) && !/FxiOS/.test(userAgent)) {
      return true;
    }
  }
  return false;
}

export function isWindowsOS() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  console.log('userAgent', userAgent);
  // This regular expression checks for the "Windows" keyword in the userAgent string.
  return /Windows/.test(userAgent);
}

export function orderButton(order) {
  let statuses = {};
  if (order.kind === 'delivery') {
    statuses = {
      created: { action: 'cook', name: 'Готувати', status: 'Створено', color: 'btn-info' },
      payed: { action: 'cook', name: 'Готувати', status: 'Створено', color: 'btn-info' },
      cooking: { action: 'cooked', name: 'Приготовлено', status: 'Готується', color: 'btn-success' },
      ready: { action: 'delivering', name: 'В дорозі', status: 'Приготовлено', color: 'btn-danger' },
      on_delivery: { action: 'done', name: 'Завершено', status: 'В дорозі', color: 'btn-dark' },
      completed: { action: 'completed', name: 'Завершено', status: 'Завершено', color: 'btn-dark' },
      declined_by_restaurant: { action: 'restaurant_decline', name: 'Скасовано', status: 'Скасовано', color: 'btn-light' },
      declined_by_user: { action: 'restaurant_decline', name: 'Скасовано', status: 'Скасовано', color: 'btn-light' },
      refunded: { action: 'refund', name: 'Повернено', status: 'Повернено', color: 'btn-light' }
    };
  } else {
    statuses = {
      created: { action: 'cook', name: 'Готувати', status: 'Створено', color: 'btn-info' },
      payed: { action: 'cook', name: 'Готувати', status: 'Створено', color: 'btn-info' },
      cooking: { action: 'cooked', name: 'Приготовлено', status: 'Готується', color: 'btn-success' },
      ready: { action: 'done', name: 'Завершити', status: 'Приготовлено', color: 'btn-dark' },
      on_delivery: { action: 'completed', name: 'В дорозі', status: 'В дорозі', color: 'btn-dark' },
      completed: { action: 'completed', name: 'Завершено', status: 'Завершено', color: 'btn-dark' },
      declined_by_restaurant: { action: 'restaurant_decline', name: 'Скасовано', status: 'Скасовано', color: 'btn-light' },
      declined_by_user: { action: 'restaurant_decline', name: 'Скасовано', status: 'Скасовано', color: 'btn-light' },
      refunded: { action: 'refund', name: 'Повернено', status: 'Повернено', color: 'btn-light' }
    };
  }
  return statuses[order.status];
}

export function sendPushDeviceToken(token, device) {
  console.log('current token for client: ', token);
  indexedDBService.setNotificationData(`${device}Token`, token);

  return indexedDBService.getUserData('userDetails').then(userDetails => {
    const userDevices = !!userDetails.userData && !!userDetails.userData.devices;
    if (!!userDevices && !userDetails.userData.devices.includes(token)) {
      return sendPushToken(token, device).then((resp) => {
        saveProfileInLocalStorage(resp.data.user);
      }).catch((err) => {
        console.log('An error occurred while sending token. ', err);
      });
    }
  });
}

export function alreadyPaidPayCheck(payCheck) {
  return payCheck.status === 'paid' || payCheck.status === 'terminal_paid' || payCheck.status === 'cash_paid' ||
    payCheck.status === 'mix_paid';
}

export const isFirebaseSupported = () => {
  // Check for Service Worker support
  if (!('serviceWorker' in navigator)) {
    console.log('Service Workers are not supported by this browser.');
    return false;
  }

  // Check for Push API support
  if (!('PushManager' in window)) {
    console.log('Push messaging is not supported by this browser.');
    return false;
  }

  // Check for Notification API support
  if (!('Notification' in window)) {
    console.log('Notifications are not supported by this browser.');
    return false;
  }

  // // Check if Notification permission is granted
  // if (Notification.permission === 'denied') {
  //   console.log('User has blocked notifications.');
  //   return false;
  // }

  return true;
};

export function orderRestaurants(slug) {
  const state = store.getState();
  const orders = Object.values(state.orders.orderDishes);
  const company = state.restaurants?.companies?.[slug];
  const companyRestSlugs = company?.restaurants ? Object.values(company.restaurants).map(r => r.slug) : [];

  return orders.map((o) => {
    if (!o || !o.restaurant) return null;
    const isCompMatch = !!o.company && o.company.slug === slug;
    const isRestInComp = companyRestSlugs.includes(o.restaurant.slug);
    return (isCompMatch || isRestInComp) ? o.restaurant : null;
  }).filter(Boolean);
}

export function isHoliday(restaurant) {
  return !restaurant.working_hours.default.always_open && restaurant.working_hours.default.
    working_hours[new Date().toLocaleString('en-us', { weekday: 'short' })].holiday;
}

export function cutFloat(value) {
  return (Math.floor(value * 100) / 100);
}

export function isValidURL(str) {
  let a = document.createElement('a');
  a.href = str;
  return (a.host && a.host !== window.location.host);
}

export function isBoundsInside(bounds1, bounds2) {
  const sw1 = bounds1._southWest;
  const ne1 = bounds1._northEast;

  const sw2 = bounds2._southWest;
  const ne2 = bounds2._northEast;

  return sw2.lat >= sw1.lat && sw2.lng >= sw1.lng &&
    ne2.lat <= ne1.lat && ne2.lng <= ne1.lng;
}

export function isPointInsideBounds(latitude, longitude, bounds) {
  if (!bounds || !latitude || !longitude) return false;
  let sw, ne;
  if (bounds._southWest && bounds._northEast) {
    sw = bounds._southWest;
    ne = bounds._northEast;
  } else if (typeof bounds.getSouthWest === 'function' && typeof bounds.getNorthEast === 'function') {
    sw = bounds.getSouthWest();
    ne = bounds.getNorthEast();
  } else if (Array.isArray(bounds) && bounds.length === 2) {
    sw = { lat: bounds[0][0], lng: bounds[0][1] };
    ne = { lat: bounds[1][0], lng: bounds[1][1] };
  } else {
    return false;
  }
  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);
  return lat >= sw.lat && lat <= ne.lat && lng >= sw.lng && lng <= ne.lng;
}

export function formatIsoDate(isoString) {
  const date = new Date(isoString);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}.${month}.${year} ${hours}:${minutes}`;
}

export function toggleRsbsInteractivity(disabled) {
  const elements = document.querySelectorAll(
    '[data-rsbs-backdrop], [data-rsbs-overlay], [data-rsbs-root]'
  );

  elements.forEach(el => {
    el.style.pointerEvents = disabled ? 'none' : '';
  });

  // Also make sure body scroll-lock isn't interfering
  const body = document.querySelector('body');
  if (body) {
    body.style.pointerEvents = disabled ? 'auto' : '';
  }
}

export function triggerLocationSearch() {
  const searchInput = document.querySelector(
    '.geocoder-control-input, input.geocoder-control-input, .geosearch-input, .leaflet-control-geosearch input, input.glass, .leaflet-control-geocoder input, .leaflet-control-container input[type="text"], .leaflet-control input'
  );
  const searchBtn = document.querySelector(
    '.geocoder-control-icon, .leaflet-control-geosearch-button, .geocoder-control-button, .geocoder-control, .glass'
  );

  if (searchBtn) {
    try { searchBtn.click(); } catch (e) { }
  }
  if (searchInput) {
    try {
      searchInput.focus();
      searchInput.click();
    } catch (e) { }
  }
}